import {
  useDispatchGetCompatibilityReport,
  useSelectCompatibilityReportGraphs,
} from 'affiny-core/hooks';
import { ECompatibilityReportType } from 'affiny-core/modules/api/models/member/member.types';
import { ICompatibilityReportTabProps } from 'affiny-core/modules/compatibilityReport/types';
import {
  Box,
  Flex,
  PersonalityGraph,
  rem,
  ShadowBox,
  Text,
} from 'affiny-ui/src';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { CompatibilityReportChapterNavigation } from './CompatibilityReportChapterNavigation';
import { CompatibilityReportIntro } from './CompatibilityReportIntro';

interface ICompatibilityReportPersonalitiesChapter
  extends ICompatibilityReportTabProps {
  reportType: ECompatibilityReportType;
  setSection: (arg: string) => void;
}

export const CompatibilityReportPersonalitiesChapter = ({
  score,
  nickname,
  reportType,
  setSection,
  wantedGender,
  accountId,
}: ICompatibilityReportPersonalitiesChapter) => {
  useDispatchGetCompatibilityReport(accountId, reportType);

  const { t } = useTranslation();

  const graphs = useSelectCompatibilityReportGraphs(
    ECompatibilityReportType.PERSONALITY,
    accountId
  );

  const goToPreviousChapter = () => setSection('visions');

  const renderContent = () => {
    return (
      <>
        {graphs.map(graph => {
          return (
            <Flex key={graph.id} mt="xLarge" alignItems="flex-end">
              <ShadowBox
                width={rem(352)}
                mr="xLarge"
                px="large"
                pt="medium"
                pb="large"
              >
                <Text mb="medium">
                  {t(`compatibilityReport.personality.graphLabels.${graph.id}`)}
                </Text>
                <Flex justifyContent="space-around">
                  {wantedGender &&
                    graph.matrices.map((matrice, idx) => {
                      return (
                        <PersonalityGraph
                          key={`matrice.${idx}`}
                          topTitle={t(
                            `compatibilityReport.personality.graphLabels.${matrice.translationKeys[0]}`
                          )}
                          bottomTitle={t(
                            `compatibilityReport.personality.graphLabels.${matrice.translationKeys[1]}`
                          )}
                          rightValue={matrice.viewee.y || 0}
                          leftValue={matrice.viewer.y || 0}
                          myLabel={t(
                            'compatibilityReport.graphs.me'
                          ).toUpperCase()}
                          wantedLabel={t('compatibilityReport.graphs.other', {
                            WANTED_GENDER: wantedGender,
                          }).toUpperCase()}
                        />
                      );
                    })}
                </Flex>
              </ShadowBox>
              <Box width={rem(544)}>
                <Text fontSize={rem(18)} fontWeight={600} mb="medium">
                  {t(`compatibilityReport.personality.analysis`)}
                </Text>
                <Text>
                  {nickname &&
                    graph.analysis &&
                    graph.analysis[0]?.replace(/\[UL_PSEUDO\]/gm, nickname)}
                </Text>
              </Box>
            </Flex>
          );
        })}
      </>
    );
  };

  return (
    <Flex px={rem(96)} flexDirection="column">
      {score && nickname && (
        <CompatibilityReportIntro
          reportType={reportType}
          score={score}
          nickname={nickname}
        />
      )}
      <Text fontSize={rem(24)} fontWeight={600} mt="xLarge">
        {t(`compatibilityReport.personality.subTitle`)}
      </Text>
      {renderContent()}
      <CompatibilityReportChapterNavigation
        goToPreviousChapter={goToPreviousChapter}
        textPreviousChapter={t('compatibilityReport.tabs.visions')}
      />
    </Flex>
  );
};
